/**
 * Corramet Corrugated Roof Calculator
 * Reads from window.roofCalcData (pre-filled by Liquid section)
 *
 * PURE METAOBJECT INTERPRETER:
 *  - variantDrivenBy selects the variant
 *  - qtyFormula computes the quantity
 *  - No business logic in JS — all driven by metaobject values
 *  - Promo messages render inline beneath any rule that has one
 *
 * SHEET CALC:
 *  - Width: CEIL(A / (sheetWidthMm - sheetWidthOverlapMm))
 *  - Length: minimum-wastage split with sheetLengthOverlapMm
 *  - Variant matching uses colour + length (sheets are coloured: Black/Green)
 *
 * FLASHING CALC:
 *  - Smallest variant covering measurement, or split if needed (no overlap)
 *  - Variant matching uses colour + length
 *
 * IMAGE NAMING: corramet-{roofKeyDashed}-{colour}.webp
 */

(function () {
  'use strict';

  var data = window.roofCalcData;
  if (!data) {
    console.warn('Roof calculator: no data found. Check Liquid section.');
    return;
  }

  // ─── State ───────────────────────────────────────────────────────────
  var state = {
    tag1: '',
    rfnm: 1,
    roofKey: '',
    roofName: '',
    colour: '',            // blank by default — user must select
    sizeA: 0,
    sizeB: 0,
    sheetsAcross: 0,
    lengthRun: [],         // set by sheet rule; read by fixings rule
    cartItems: []
  };


  // ─── Split functions ─────────────────────────────────────────────────

  // Flashing split (no overlap — butt join)
  function splitLength(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function(a,b){ return a.maxLength - b.maxLength; });
    var results = [];
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        results.push({ variantLabel: sorted[i].variantLabel, quantity: 1 });
        return results;
      }
    }
    var longest = sorted[sorted.length - 1];
    var fullLengths = Math.floor(measurementMm / longest.maxLength);
    var remainder = measurementMm % longest.maxLength;
    if (fullLengths > 0) {
      results.push({ variantLabel: longest.variantLabel, quantity: fullLengths });
    }
    if (remainder > 0) {
      for (var j = 0; j < sorted.length; j++) {
        if (remainder <= sorted[j].maxLength) {
          results.push({ variantLabel: sorted[j].variantLabel, quantity: 1 });
          break;
        }
      }
    }
    return results;
  }

  // Sheet split (with overlap)
  // Tiebreak: fewest sheets → fewest unique variants → lowest wastage → lowest total
  function buildCombinations(variantsMm, maxDepth) {
    var results = [];
    var seen = {};
    function recurse(current, startIdx) {
      if (current.length >= 1) {
        var key = current.slice().sort(function(a,b){return a-b;}).join(',');
        if (!seen[key]) { seen[key] = true; results.push(current.slice()); }
      }
      if (current.length >= maxDepth) return;
      for (var i = startIdx; i < variantsMm.length; i++) {
        current.push(variantsMm[i]); recurse(current, i); current.pop();
      }
    }
    recurse([], 0);
    return results;
  }

  function splitSheetLength(tiers, measurementMm, overlapMm) {
    var variantsMm = tiers.map(function(t){ return t.maxLength; }).sort(function(a,b){ return a-b; });
    var labelByMm = {};
    for (var i = 0; i < tiers.length; i++) labelByMm[tiers[i].maxLength] = tiers[i].variantLabel;

    var combos = buildCombinations(variantsMm, 5);
    var best = null, bestCount = Infinity, bestUnique = Infinity, bestWaste = Infinity, bestTotal = Infinity;

    for (var c = 0; c < combos.length; c++) {
      var combo = combos[c], sumLen = 0;
      for (var k = 0; k < combo.length; k++) sumLen += combo[k];
      var coverage = sumLen - (combo.length - 1) * overlapMm;
      if (coverage < measurementMm) continue;
      var waste = coverage - measurementMm;
      var uniqueMap = {};
      for (var u = 0; u < combo.length; u++) uniqueMap[combo[u]] = true;
      var uniqueCount = Object.keys(uniqueMap).length;
      var better = false;
      if (combo.length < bestCount) better = true;
      else if (combo.length === bestCount && uniqueCount < bestUnique) better = true;
      else if (combo.length === bestCount && uniqueCount === bestUnique && waste < bestWaste) better = true;
      else if (combo.length === bestCount && uniqueCount === bestUnique && waste === bestWaste && sumLen < bestTotal) better = true;
      if (better) { best = combo; bestCount = combo.length; bestUnique = uniqueCount; bestWaste = waste; bestTotal = sumLen; }
    }

    if (!best) return [];
    var run = [];
    for (var j = 0; j < best.length; j++) run.push({ variantLabel: labelByMm[best[j]], mm: best[j] });
    return run;
  }


  // ─── Helpers ─────────────────────────────────────────────────────────

  function getOverride(rule) {
    var overrides = rule.roofTypeOverrides;
    if (typeof overrides === 'string') {
      try { overrides = JSON.parse(overrides); } catch (e) { return null; }
    }
    if (!overrides) return null;
    return overrides[state.roofKey] || null;
  }

  function getProduct(rule) {
    var products = rule.products;
    if (!products || products.length === 0) return null;
    return products[0];
  }

  function findVariantByLabel(product, label) {
    if (!product || !product.variants) return null;
    var normLabel = label.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (normTitle.indexOf(normLabel) !== -1) return product.variants[i];
    }
    return null;
  }

  // Match by colour + length (used for both coloured sheets and flashings)
  function findColouredVariant(product, lengthLabel, colour) {
    if (!product || !product.variants) return null;
    var normLen = lengthLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    var normCol = colour.replace(/\s/g, '').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (normTitle.indexOf(normLen) !== -1 && normTitle.indexOf(normCol) !== -1) return product.variants[i];
    }
    return findVariantByLabel(product, lengthLabel);
  }


  // ─── Core calculation ────────────────────────────────────────────────

  function calculateKit() {
    state.cartItems = [];
    state.lengthRun = [];
    state.sheetsAcross = Math.ceil(state.sizeA / (data.sheetWidthMm - data.sheetWidthOverlapMm));

    for (var i = 0; i < data.productRules.length; i++) {
      if (processRule(data.productRules[i]) === 'maxlength') return;
    }

    renderKitList();
  }

  function processRule(rule) {
    var override = getOverride(rule);
    if (!override) return;

    var product = getProduct(rule);
    if (!product) return;

    var formula = rule.qtyFormula;
    var variantDrivenBy = rule.variantDrivenBy;
    var apexMultiplier = override.multiplier || 1;
    var quantity = 0;
    var variant = null;

    // ── Sheet split (coloured sheets + length) ──
    if (variantDrivenBy === 'size_b_split_length' && formula === 'ceil_a_div_coverage') {
      state.lengthRun = splitSheetLength(rule.lengthTiers, state.sizeB, data.sheetLengthOverlapMm);
      if (state.lengthRun.length === 0) { showMaxLengthMessage(); return 'maxlength'; }
      var bucket = {};
      for (var k = 0; k < state.lengthRun.length; k++) {
        bucket[state.lengthRun[k].variantLabel] = (bucket[state.lengthRun[k].variantLabel] || 0) + 1;
      }
      for (var lbl in bucket) {
        if (!bucket.hasOwnProperty(lbl)) continue;
        variant = findColouredVariant(product, lbl, state.colour);
        if (!variant) continue;
        state.cartItems.push({
          rule: rule, product: product, variant: variant,
          quantity: state.sheetsAcross * bucket[lbl] * apexMultiplier, editable: true
        });
      }
      return;
    }

    // ── Flashing split × B (no overlap) ──
    if (variantDrivenBy === 'size_b_split_length' && formula === 'split_length_from_b') {
      var splitsB = splitLength(rule.lengthTiers, state.sizeB);
      for (var sb = 0; sb < splitsB.length; sb++) {
        variant = findColouredVariant(product, splitsB[sb].variantLabel, state.colour);
        if (!variant) continue;
        state.cartItems.push({
          rule: rule, product: product, variant: variant,
          quantity: splitsB[sb].quantity * apexMultiplier, editable: true
        });
      }
      return;
    }

    // ── Flashing split × A (no overlap) ──
    if (variantDrivenBy === 'size_a_split_length' && formula === 'split_length_from_a') {
      var splitsA = splitLength(rule.lengthTiers, state.sizeA);
      for (var sa = 0; sa < splitsA.length; sa++) {
        variant = findColouredVariant(product, splitsA[sa].variantLabel, state.colour);
        if (!variant) continue;
        state.cartItems.push({
          rule: rule, product: product, variant: variant,
          quantity: splitsA[sa].quantity * apexMultiplier, editable: true
        });
      }
      return;
    }

    // ── Fixed variant selection (foam, fixings, ridge endcap, tape) ──
    if (variantDrivenBy === 'fixed') {
      variant = rule.fixedVariantLabel
        ? findVariantByLabel(product, rule.fixedVariantLabel)
        : product.variants[0];
    }

    if (!variant) return;

    // ── Quantity formula ───────────────────────────────────────────────

    if (formula === 'fixings_per_sheet_run') {
      var totalFixings = 0;
      for (var m = 0; m < state.lengthRun.length; m++) {
        totalFixings += 10 * (state.lengthRun[m].mm / 1000);
      }
      quantity = Math.ceil(totalFixings * state.sheetsAcross * apexMultiplier / 10);

    } else if (formula === 'per_sheet') {
      quantity = state.sheetsAcross * (rule.perSheetQty || 1) * apexMultiplier;

    } else if (formula === 'ceil_measurement_div_10000') {
      var measurement = 0;
      if (override.measurement === 'a')             measurement = state.sizeA;
      else if (override.measurement === 'a_plus_b') measurement = state.sizeA + state.sizeB;
      else if (override.measurement === 'b')         measurement = state.sizeB;
      else if (override.measurement === 'b_times_2') measurement = state.sizeB * 2;
      quantity = Math.ceil(measurement / 10000);
      if (quantity < 1) quantity = 1;

    } else if (formula === 'fixed') {
      quantity = override.fixed_qty || 0;
    }

    if (quantity > 0) {
      state.cartItems.push({
        rule: rule, product: product, variant: variant,
        quantity: quantity, editable: true
      });
    }
  }


  // ─── Kit list rendering ──────────────────────────────────────────────

  function renderKitList() {
    var container = document.querySelector('.dkit .kit-list');
    var mcontainer = document.querySelector('.mkit .kit-list');
    if (!container) { console.warn('Roof calculator: .kit-list container not found'); return; }

    var html = '', mhtml = '', total = 0, totalSaved = 0;

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      var price = item.variant.price / 100;
      var compareAt = item.variant.compareAtPrice ? item.variant.compareAtPrice / 100 : null;
      var lineTotal = price * item.quantity;
      var lineSaving = compareAt ? (compareAt - price) * item.quantity : 0;
      total += lineTotal;
      totalSaved += lineSaving;

      var image = '';
      if (item.variant && item.variant.image) image = item.variant.image;
      else if (item.product && item.product.images && item.product.images.length > 0) image = item.product.images[0];

      // Desktop
      html += '<div class="kit-item" data-index="' + i + '">';
      html += '  <div class="kit-item-image">';
      if (image) html += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      html += '  </div>';
      html += '  <div class="kit-item-details">';
      html += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      html += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      html += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        html += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        html += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        html += '    <span>' + formatMoney(price) + '</span>';
      }
      html += '  </div></div>';
      html += '  <div class="kit-item-qty">';
      html += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      html += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      html += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      html += '  </div>';
      html += '  <div class="kit-item-price">' + formatMoney(lineTotal) + '</div>';
      html += '</div>';

      // Mobile
      mhtml += '<div class="kit-item" data-index="' + i + '">';
      mhtml += '  <div class="w-100"><div class="w-25"><div class="kit-item-image">';
      if (image) mhtml += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      mhtml += '  </div></div><div class="w-75">';
      mhtml += '  <div class="kit-item-details">';
      mhtml += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      mhtml += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      mhtml += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        mhtml += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        mhtml += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        mhtml += '    <span>' + formatMoney(price) + '</span>';
      }
      mhtml += '  </div></div>';
      mhtml += '  <div class="kit-item-qty">';
      mhtml += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      mhtml += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      mhtml += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      mhtml += '  </div></div></div>';
      mhtml += '</div>';

      // Promo message — rendered beneath any rule that has one
      if (item.rule.promoMessage) {
        html += '<div class="kit-promo-message"><span>✓ ' + item.rule.promoMessage + '</span></div>';
        mhtml += '<div class="kit-promo-message"><span>✓ ' + item.rule.promoMessage + '</span></div>';
      }
    }

    container.innerHTML = html;
    mcontainer.innerHTML = mhtml;
    $('.kitcart .total span').html(formatMoney(total));
    if (totalSaved > 0) {
      var ysav = 'You saved ';
      if (window.location.pathname.indexOf('fr-fr') === 1) { ysav = 'Vous avez enregistré '; }
      $('.kitcart .saved').show().html(ysav + formatMoney(totalSaved) + '!');
    } else {
      $('.kitcart .saved').hide();
    }
    showStep('kit');
    bindKitListEvents();
  }

  function bindKitListEvents() {
    document.querySelectorAll('.kit-qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        if (state.cartItems[idx].quantity > 0) { state.cartItems[idx].quantity--; renderKitList(); }
      });
    });
    document.querySelectorAll('.kit-qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        state.cartItems[idx].quantity++;
        renderKitList();
      });
    });
    document.querySelectorAll('.kit-qty-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        var val = parseInt(this.value) || 0;
        if (val < 0) val = 0;
        state.cartItems[idx].quantity = val;
        renderKitList();
      });
    });
    document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.kit-add-to-cart').forEach(function (b) {
          b.setAttribute('disabled', 'disabled');
          b.textContent = window.location.pathname.indexOf('fr-fr') === 1 ? 'Ajout au panier...' : 'Adding to cart...';
        });
        addToCart();
      });
    });
  }

  function formatMoney(amount) {
    var currency = data.currency || {};
    try {
      return new Intl.NumberFormat(currency.locale || 'en-GB', { style: 'currency', currency: currency.code || 'GBP' }).format(amount);
    } catch (e) {
      return (currency.symbol || '£') + amount.toFixed(2);
    }
  }


  // ─── Cart submission ──────────────────────────────────────────────────

  function addToCart() {
    var items = [];
    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (item.quantity < 1) continue;
      items.push({
        quantity: item.quantity,
        id: item.variant.id,
        properties: {
          '_ROOF TYPE': state.roofName,
          '_COLOUR': state.colour,
          '_LENGTH A': state.sizeA,
          '_LENGTH B': state.sizeB
        }
      });
    }
    if (items.length === 0) {
      alert('No items to add. Please check your quantities.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled'); btn.textContent = 'Add full kit to cart';
      });
      return;
    }
    items.reverse();
    jQuery.post('/cart/add.js', { items: items }).then(function () {
      window.location.href = window.location.pathname.indexOf('fr-fr') === 1 ? '/fr-fr/cart' : '/cart';
    }).fail(function () {
      alert('There was an error adding to cart. Please try again.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled'); btn.textContent = 'Add full kit to cart';
      });
    });
  }


  // ─── Max length message ───────────────────────────────────────────────

  function showMaxLengthMessage() {
    alert(data.maxLengthMessage || 'This size is too long, please get free advice on Live Chat');
  }


  // ─── UI step management ───────────────────────────────────────────────

  function showStep(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();
    if (step === 'rooftype') { $('.rooftype').show(); updateStepIndicators('wtor'); }
    else if (step === 'roofsheet') { $('.roofsheet').show(); updateStepIndicators('yrc'); }
    else if (step === 'roofsize') { $('.roofsize').show(); updateStepIndicators('cyrs'); }
    else if (step === 'kit') { $('.kit-list-wrapper').show(); updateStepIndicators('kit'); }
  }

  function updateStepIndicators(currentStep) {
    $(".roofcal .number").removeClass("current").removeClass("complete");
    var steps = ['wtor', 'yrc', 'cyrs', 'kit'];
    var currentIdx = steps.indexOf(currentStep);
    for (var i = 0; i < steps.length; i++) {
      if (i < currentIdx) $(".roofcal .number." + steps[i]).addClass("complete");
      else if (i === currentIdx) $(".roofcal .number." + steps[i]).addClass("current");
    }
  }


  // ─── Image swap helper ────────────────────────────────────────────────

  function buildImagePath(currentSrc) {
    if (!state.roofKey || !state.colour) return currentSrc;
    var roofSegment = state.roofKey.replace(/_/g, '-');
    var newFilename = data.calcKey + '-' + roofSegment + '-' + state.colour.toLowerCase() + '.webp';
    return currentSrc.replace(/[^\/]+\.(webp|jpg|jpeg|png)/i, newFilename);
  }


  // ─── Event bindings ───────────────────────────────────────────────────

  $(".roofcal .roofimg").click(function () {
    var roofIndex = parseInt($(this).attr('key')) - 1;
    var roofType = data.roofTypes[roofIndex];
    if (!roofType) return;
    state.roofKey = roofType.key;
    state.roofName = roofType.name;
    state.tag1 = $(this).attr('title');
    state.rfnm = $(this).attr('key');
    // Use static metaobject image until colour is selected
    var staticImage = roofType.image || $(this).find('img').attr('src');
    $('.roofsheet img').attr('key', staticImage);
    $('.roofsheet img, .roofsize img, .qimg img').attr('src', staticImage);
    showStep('roofsheet');
  });

  $('.srtype').click(function () {
    state.roofKey = ''; state.roofName = ''; showStep('rooftype');
  });

  $('select[name="colour1"]').change(function () {
    state.colour = $(this).val();
    $('.quote .clr').text(this.options[this.selectedIndex].text);
    var baseImg = $(".roofsheet img").attr("key");
    if (baseImg) {
      var newImage = buildImagePath(baseImg);
      $(".roofsheet img, .roofsize img, .qimg img").attr("src", newImage);
    }
  });

  $('.rfsize').click(function () {
    if (!state.roofKey) { if(window.location.pathname.indexOf('fr-fr') > 0){ alert('Veuillez sélectionner un type de toiture'); }else{ alert('Please select a Roof Type'); }; return false; }
    if (!state.colour) { if(window.location.pathname.indexOf('fr-fr') > 0){ alert('Veuillez sélectionner une couleur'); }else{ alert('Please select a Roof Sheet Colour'); };  return false; }
    showStep('roofsize');
  });

  $(".roofcal .yrc").click(function () {
    if (!state.roofKey) return false;
    showStep('roofsheet');
  });

  $('.ckit').click(function () {
    $(this).attr('disabled', 'disabled');
    state.sizeA = parseInt($('.roofinput input[name="lengtha"]').val()) || 0;
    state.sizeB = parseInt($('.roofinput input[name="lengthb"]').val()) || 0;
    if (state.sizeA < 1000 || state.sizeB < 1000) {
      alert(data.minSizeMessage || 'Please enter valid length and width (minimum 1000mm)');
      $(this).attr('disabled', false);
      return false;
    }
    $('.quote .sizea span').text(state.sizeA);
    $('.quote .sizeb span').text(state.sizeB);
    calculateKit();
    $(this).attr('disabled', false);
  });

  $(".roofinput input").change(function () {
    $(".ckit").attr("disabled", false);
  });

})();
